import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Alert, Paper, Button, Divider } from '@mui/material';
import { Download, GetApp, Logout } from '@mui/icons-material';
import { ClientSelector } from './components/ClientSelector';
import { DashboardChart } from './components/DashboardChart';
import { ClientSummaryTable } from './components/ClientSummaryTable';
import { NetworkSummaryTable } from './components/NetworkSummaryTable';
import { AnalysisTable } from './components/AnalysisTable';
import { FontSelector } from './components/FontSelector';
import { CorsTest } from './components/CorsTest';
import { LoginPage } from './components/LoginPage';
import { HomePage } from './components/HomePage';
import { s3Service } from './services/s3Service';
import { downloadService } from './services/downloadService';
import { authService } from './services/authService';
import { parseCSV, parseSummaryCSV, parseFailureBreakdown, CSVData } from './utils/csvParser';
import { appConfig } from './config/app-config';
import { IceDashboard } from './components/IceDashboard';
import { awsConfig } from './config/aws-config';

interface ChartData {
  successData: {
    labels: string[];
    datasets: {
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  };
  failureTypes: {
    labels: string[];
    datasets: {
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  };
}

// interface SummaryData {
//   success_count: number;
//   failure_count: number;
// }

// interface FailureData {
//   [key: string]: number;
// }

// Client display name mapping (same as in ClientSelector)
const CLIENT_DISPLAY_NAMES: { [key: string]: string } = {
  'payu_prod': 'PayU',
  'spinny_b2c_prod': 'Spinny B2C', 
  'cars24_prod': 'Cars24',
  'indmoney': 'IndMoney',
  'sk_finance_prod': 'SK Finance',
  'attestr': 'Attestr',
  'autorox': 'AutoRox',
  'ice_vehicle_prod_user': 'ICE Vehicle Lookup'
};

// Get user-friendly name or fallback to technical name
const getClientDisplayName = (technicalName: string): string => {
  return CLIENT_DISPLAY_NAMES[technicalName] || technicalName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [currentView, setCurrentView] = useState<'homepage' | 'engateway' | 'ice'>('homepage');
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [clientData, setClientData] = useState<CSVData[]>([]);
  const [networkData, setNetworkData] = useState<CSVData[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [fontFamily, setFontFamily] = useState<string>(appConfig.defaultFont);

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      setAuthLoading(false);
    };

    // Small delay to show loading state
    setTimeout(checkAuth, 100);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    // Reset dashboard state
    setCurrentView('homepage');
    setChartData(null);
    setClientData([]);
    setNetworkData([]);
    setSelectedFolder('');
    setError('');
    setSuccess('');
  };

  // const handleNavigateToEnGateway = () => {
  //   setCurrentView('engateway');
  // };

  const handleNavigateToHomepage = () => {
    setCurrentView('homepage');
    // Reset EN Gateway dashboard state when going back to homepage
    setChartData(null);
    setClientData([]);
    setNetworkData([]);
    setSelectedFolder('');
    setError('');
    setSuccess('');
  };

  const handleClientSelection = async (client: string, month: string, year: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Construct folder name like HTML dashboard: logs_${client}_${month}-${year}
    const folder = `logs_${client}_${month}-${year}`;
    setSelectedFolder(folder);
    
    // Get month name for display
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const monthName = monthNames[parseInt(month) - 1];
    
    try {
      // Load both client and network data
      const { clientContent, networkContent } = await s3Service.loadDashboardData(folder);
      
      if (!clientContent && !networkContent) {
        setError(`🔴 No data found for ${client}, ${monthName} ${year}.`);
        setChartData(null);
        setClientData([]);
        setNetworkData([]);
        return;
      }
      
      // Parse CSV content
      const parsedClientData = clientContent ? parseCSV(clientContent) : [];
      const parsedNetworkData = networkContent ? parseCSV(networkContent) : [];
      
      // Update state
      setClientData(parsedClientData);
      setNetworkData(parsedNetworkData);
      
      // Create chart data for the original pie charts
      if (parsedClientData.length > 0) {
        const summaryData = parseSummaryCSV(clientContent);
        const failureBreakdown = parseFailureBreakdown(clientContent);
        
        const successData = {
          labels: ['Success', 'Failure'],
          datasets: [{
            data: [
              summaryData.successful_requests || 0,
              summaryData.failed_requests || 0
            ],
            backgroundColor: ['#4CAF50', '#f44336'],
            borderColor: ['#388E3C', '#D32F2F'],
            borderWidth: 1
          }]
        };

        const failureTypes = {
          labels: Object.keys(failureBreakdown),
          datasets: [{
            data: Object.values(failureBreakdown),
            backgroundColor: [
              '#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3',
              '#9c27b0', '#795548', '#607d8b'
            ],
            borderColor: [
              '#d32f2f', '#f57c00', '#fbc02d', '#388e3c', '#1976d2',
              '#7b1fa2', '#5d4037', '#455a64'
            ],
            borderWidth: 1
          }]
        };

        setChartData({ successData, failureTypes });
      }
      
      setSuccess(`✅ Showing logs for ${client}, ${monthName} ${year}`);
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      setError(`🔴 Error loading data for ${client}, ${monthName} ${year}: ${error.message}`);
      setChartData(null);
      setClientData([]);
      setNetworkData([]);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ fontFamily }}>Loading...</Typography>
      </Container>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} fontFamily={fontFamily} />;
  }

  // Show homepage or dashboard based on current view
  if (currentView === 'homepage') {
    return (
      <HomePage 
        fontFamily={fontFamily}
        onNavigateToEnGateway={() => setCurrentView('engateway')}
        onNavigateToIce={() => setCurrentView('ice')}
      />
    );
  }

  // Show ICE dashboard
  if (currentView === 'ice') {
    return (
      <IceDashboard 
        fontFamily={fontFamily}
        onNavigateToHomepage={() => setCurrentView('homepage')}
      />
    );
  }

  // EN Gateway Dashboard View
  return (
    <Container maxWidth="xl" sx={{ fontFamily }}>
      <Box sx={{ my: 4, fontFamily }}>
        {/* Header with title and user info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleNavigateToHomepage}
              sx={{ fontFamily }}
            >
              ← Back to Home
            </Button>
          </Box>
          
          <Typography variant="h4" component="h1" sx={{ fontFamily, flexGrow: 1, textAlign: 'center' }}>
            📊 EN Gateway Dashboard
          </Typography>
          
          {authService.isAuthEnabled() && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary' }}>
                  Welcome, <strong>{authService.getCurrentUser()}</strong>
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Logout />}
                onClick={handleLogout}
                size="small"
                sx={{ fontFamily }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Box>
        
        <FontSelector selectedFont={fontFamily} onFontChange={setFontFamily} />
        
        {appConfig.enableCorsTest && (
          <CorsTest fontFamily={fontFamily} />
        )}
        
        <ClientSelector onSelectionChange={handleClientSelection} fontFamily={fontFamily} />

        {/* Status Messages */}
        {loading && (
          <Alert severity="info" sx={{ mb: 3, fontFamily }}>
            Loading dashboard data...
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, fontFamily }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3, fontFamily }}>
            {success}
          </Alert>
        )}

        {/* Navigation Breadcrumb */}
        {(clientData.length > 0 || networkData.length > 0) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily }}>
              📍 Dashboard &gt; EN Gateway &gt; {clientData.length > 0 ? getClientDisplayName(String(clientData[0].client || '')) : 'Select a Client'}
              {selectedFolder && ` > ${selectedFolder.split('/').pop()}`}
            </Typography>
          </Box>
        )}

        {/* Download Raw S3 Files */}
        {selectedFolder && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
              📥 Download Raw Data
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadService.downloadFromS3(selectedFolder, 'activity_all.csv')}
                sx={{ fontFamily }}
                color="primary"
              >
                All Raw Data
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadService.downloadFromS3(selectedFolder, 'activity_success.csv')}
                sx={{ fontFamily }}
                color="primary"
              >
                Successful Transactions
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadService.downloadFromS3(selectedFolder, 'activity_failure.csv')}
                sx={{ fontFamily }}
                color="primary"
              >
                Failed Transactions
              </Button>
              {awsConfig.showNetworkLogs && (
                <>
                  <Divider sx={{ width: '100%', my: 1 }} />
                  <Typography variant="subtitle2" sx={{ width: '100%', fontFamily }}>
                    Advanced Downloads (Network Logs)
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => downloadService.downloadFromS3(selectedFolder, 'network_all.csv')}
                    sx={{ fontFamily }}
                    size="small"
                  >
                    All Network Logs
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => downloadService.downloadFromS3(selectedFolder, 'network_success.csv')}
                    sx={{ fontFamily }}
                    size="small"
                  >
                    Successful Network Calls
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => downloadService.downloadFromS3(selectedFolder, 'network_failure.csv')}
                    sx={{ fontFamily }}
                    size="small"
                  >
                    Failed Network Calls
                  </Button>
                </>
              )}
              <Divider sx={{ width: '100%', my: 1 }} />
              <Button
                variant="contained"
                startIcon={<GetApp />}
                onClick={() => downloadService.downloadAllFiles(selectedFolder)}
                sx={{ fontFamily }}
                color="secondary"
              >
                Download All Files
              </Button>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontFamily }}>
              💡 Download raw transaction data for detailed analysis
            </Typography>
          </Paper>
        )}

        {/* Dashboard Content */}
        {(clientData.length > 0 || networkData.length > 0) && (
          <>
            {/* Client API Summary */}
            {clientData.length > 0 && (
              <ClientSummaryTable data={clientData} fontFamily={fontFamily} />
            )}

            {/* Main Pie Charts Row */}
            {chartData && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
                <Box>
                  <DashboardChart
                    title="🎯 API Success Rate"
                    data={chartData.successData}
                    fontFamily={fontFamily}
                  />
                </Box>
                <Box>
                  <DashboardChart
                    title="📈 Failure Types Breakdown"
                    data={chartData.failureTypes}
                    fontFamily={fontFamily}
                  />
                </Box>
              </Box>
            )}

            {/* API Performance Overview Section */}
            {networkData.length > 0 && (
              <>
                <Paper elevation={3} sx={{ p: 3, mb: 3, fontFamily, minHeight: '420px' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
                    🎯 API Performance Overview
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, height: '350px' }}>
                    <Box sx={{ height: '100%' }}>
                      <DashboardChart
                        title="🔍 RC Lookup Performance"
                        data={{
                          labels: ['Complete Success', 'Data Without Mapping', 'Failed'],
                          datasets: [{
                            data: (() => {
                              const surepassData = networkData.find(row => 
                                row.sub_product === 'VEHICLE-LOOKUP_INFO' && 
                                String(row.destination_system).toLowerCase().includes('surepass')
                              );
                              return surepassData ? [
                                Number(surepassData.mapping_success) || 0,
                                Number(surepassData.data_without_mapping) || 0,
                                Number(surepassData.failed) || 0
                              ] : [0, 0, 0];
                            })(),
                            backgroundColor: ['#4CAF50', '#ff9800', '#f44336'],
                            borderColor: ['#388E3C', '#f57c00', '#D32F2F'],
                            borderWidth: 1
                          }]
                        }}
                        fontFamily={fontFamily}
                      />
                    </Box>
                    <Box sx={{ height: '100%' }}>
                      <DashboardChart
                        title="🚨 Challan Lookup Performance"
                        data={{
                          labels: ['Success', 'Failed'],
                          datasets: [{
                            data: (() => {
                              const challanData = networkData.find(row => 
                                String(row.sub_product).toLowerCase().includes('challan')
                              );
                              return challanData ? [
                                Number(challanData.success) || 0,
                                Number(challanData.failed) || 0
                              ] : [0, 0];
                            })(),
                            backgroundColor: ['#4CAF50', '#f44336'],
                            borderColor: ['#388E3C', '#D32F2F'],
                            borderWidth: 1
                          }]
                        }}
                        fontFamily={fontFamily}
                      />
                    </Box>
                  </Box>
                </Paper>
              </>
            )}

            {/* Network API Summary */}
            {networkData.length > 0 && (
              <NetworkSummaryTable data={networkData} fontFamily={fontFamily} />
            )}

            {/* Detailed API Breakdown Section */}
            {networkData.length > 0 && (
              <Paper elevation={3} sx={{ p: 3, mb: 3, fontFamily, minHeight: '420px' }}>
                <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
                  📊 Detailed API Breakdown
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, height: '350px' }}>
                  <Box sx={{ height: '100%' }}>
                    <DashboardChart
                      title="🔍 SurePass V Lookup"
                      data={{
                        labels: ['Complete Success', 'Data Without Mapping', 'Failed'],
                        datasets: [{
                          data: (() => {
                            const surepassVLookup = networkData.find(row => 
                              row.sub_product === 'VEHICLE-LOOKUP_INFO' && 
                              String(row.destination_system).toLowerCase().includes('surepass')
                            );
                            return surepassVLookup ? [
                              Number(surepassVLookup.mapping_success) || 0,
                              Number(surepassVLookup.data_without_mapping) || 0,
                              Number(surepassVLookup.failed) || 0
                            ] : [0, 0, 0];
                          })(),
                          backgroundColor: ['#4CAF50', '#ff9800', '#f44336'],
                          borderColor: ['#388E3C', '#f57c00', '#D32F2F'],
                          borderWidth: 1
                        }]
                      }}
                      fontFamily={fontFamily}
                    />
                  </Box>
                  <Box sx={{ height: '100%' }}>
                    <DashboardChart
                      title="🚨 SurePass V Challan"
                      data={{
                        labels: ['Success', 'Failed'],
                        datasets: [{
                          data: (() => {
                            const surepassChallan = networkData.find(row => 
                              String(row.sub_product).toLowerCase().includes('challan') &&
                              String(row.destination_system).toLowerCase().includes('surepass')
                            );
                            return surepassChallan ? [
                              Number(surepassChallan.success) || 0,
                              Number(surepassChallan.failed) || 0
                            ] : [0, 0];
                          })(),
                          backgroundColor: ['#4CAF50', '#f44336'],
                          borderColor: ['#388E3C', '#D32F2F'],
                          borderWidth: 1
                        }]
                      }}
                      fontFamily={fontFamily}
                    />
                  </Box>
                  <Box>
                    <DashboardChart
                      title="🔧 Digitap V Lookup"
                      data={{
                        labels: ['Success', 'Failed'],
                        datasets: [{
                          data: (() => {
                            const digitapVLookup = networkData.find(row => 
                              row.sub_product === 'VEHICLE-LOOKUP_INFO' && 
                              String(row.destination_system).toLowerCase().includes('digitap')
                            );
                            return digitapVLookup ? [
                              Number(digitapVLookup.success) || 0,
                              Number(digitapVLookup.failed) || 0
                            ] : [0, 0];
                          })(),
                          backgroundColor: ['#4CAF50', '#f44336'],
                          borderColor: ['#388E3C', '#D32F2F'],
                          borderWidth: 1
                        }]
                      }}
                      fontFamily={fontFamily}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Detailed Analysis */}
            {clientData.length > 0 && networkData.length > 0 && (
              <AnalysisTable 
                clientData={clientData} 
                networkData={networkData} 
                fontFamily={fontFamily} 
              />
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

export default App;
